/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { IAddFittingPanelFrameFacade } from '@hcworkspace/sysque/toolbelt/data-access';
import { select, Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import { selectCategories, selectSystemInfo } from '../tool-belt.selector';
import { Observable, from, of } from 'rxjs';
import { Item } from 'libs/sysque/toolbelt/data-access/src/lib/domain/item';
import { TabDescription } from 'libs/sysque/toolbelt/data-access/src/lib/domain/tab-description';
import { InitializeSystemInfo, sendPopupRoutingAction } from '../toolbelt.actions';
import { LocalStorageService } from '../../../../../utils/src/lib/local-storage/local-storage.service';
import { SysqueElectricalState } from '../../models/sysque-electrical-state';
import { BackendService } from '../../../../../utils/src/lib/sysque-backend/backend.service';
import { SystemStateManager } from '../../../../../utils/src/lib/system-state-manager.service';
import { SystemInfo } from 'libs/sysque/toolbelt/data-access/src/lib/domain/system-info';
import { TooltipItemInfo } from '../../../../../utils/src/lib/common-helper/tooltip-item-info/tooltip-item-info';
import { TooltipItem } from 'libs/sysque/toolbelt/data-access/src/lib/domain/tooltip-item';
import { GlobalConstants } from '../../constants/global-constants';
import { PastedItemsInfo } from '../../models/pasted-items-info';

@Injectable({ providedIn: 'root' })
export class AddFittingPanelFrameFacade implements IAddFittingPanelFrameFacade {
  constructor(
    private store: Store,
    private localStorageService: LocalStorageService,
    private backendService: BackendService,
    private stateManager: SystemStateManager
  ) {}

  public getDockedState(): SysqueElectricalState {
    return this.stateManager.getDockedModeState();
  }

  public getAddFittingState(): SysqueElectricalState {
    return this.stateManager.getAddFittingModeState();
  }

  public notifyGetFittingsListAction(systemName: string, categoryName: string): Observable<Item[]> {
    return from<Promise<Item[]>>(this.backendService.messageHandler.sendRequestAsync('GetFittingsBySystemNameAndCategory', [
      systemName,
      categoryName,
      false
    ]));
  }

  public notifyGetCategoryListAction(systemName: string): Observable<TabDescription[]> {
  if (!systemName) {
    return of([]);
  }

  return from(
    this.backendService.messageHandler.sendRequestAsync<TabDescription[]>(
      'GetCategories',
      [systemName]
    )
  );
}

  public selectCategories(): Observable<TabDescription[]> {
    return this.store.pipe(select(selectCategories));
  }

  public updateLocalStorage(key: string, value: any): void {
    this.localStorageService.updateStorage(key, value);
  }

  public sendPopupRoutingAction(route: string, width: number, height: number) {
    this.store.dispatch(sendPopupRoutingAction({ route, width: width, height: height }));
  }

  public saveChanges(actionName: string): void {
    this.stateManager.saveChanges(actionName);
  }

  public resetFittingModeState(): void {
    this.stateManager.resetFittingMode();
  }

  public resetManageState(): void {
    this.stateManager.resetManageMode();
  }

  public resetDockedState(): void {
    this.stateManager.getDockedModeState();
  }

  public updateSystemList(): void {
    const addFittingState = this.getAddFittingState();
    this.store.dispatch(InitializeSystemInfo());
    this.store.select(selectSystemInfo).subscribe((data) => {
      addFittingState.systemList = data;
    });

    this.stateManager.setAddFittingState(addFittingState);
  }

  public getFittings(systemName: string, categoryName: string): Observable<Item[]> {
    return from(
      this.backendService.messageHandler.sendRequestAsync<Item[]>(
        GlobalConstants.GetFittingsBySystemNameAndCategory, 
        [
          systemName,
          categoryName,
          false
        ]
      )
    );
  }

  public selectSystems(): Observable<SystemInfo[]> {
    const state = this.stateManager.getAddFittingModeState();
    return of(state.systemList);
  }

  public getTooltipItemInfo(item: Item, category: string): TooltipItem[] {
    return new TooltipItemInfo().getTooltipItemInfo(item, category);
  }

  public executePasteItemsAction(replaceItems: PastedItemsInfo[]): void {
    this.backendService.messageHandler.sendRequestAsync(GlobalConstants.LogicToExecuteForPasteItems, [replaceItems]);
  }

  public checkForDuplicateItems(items: PastedItemsInfo[]): Promise<PastedItemsInfo[]> {
    return (this.backendService.messageHandler.sendRequestAsync(GlobalConstants.CheckForDuplicateItems, [items]));
  }
}
